import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft, Sun, Moon, User, LogOut, BarChart3, BookOpen,
  GraduationCap, CheckCircle, Star, Trophy, Target, Flame,
  Activity, Award, Brain, Cpu, Loader2, TrendingUp, Beaker,
  Calendar, Zap, ChevronRight, Users, Percent
} from 'lucide-react';
import { analyticsApi } from '../lib/api';
import { toast } from 'sonner';

const COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4'];

// Simple SVG bar chart
const BarChart = ({ data, width = 320, height = 160 }) => {
  if (!data.length) return <p className="text-xs text-muted-foreground text-center py-6">No data yet</p>;
  const max = Math.max(...data.map(d => d.value), 1);
  const barW = Math.min(36, (width - 20) / data.length - 4);
  return (
    <svg width={width} height={height} className="mx-auto">
      {data.map((d, i) => {
        const barH = (d.value / max) * (height - 40);
        const x = 10 + i * (barW + 4);
        return (
          <g key={i}>
            <rect x={x} y={height - 28 - barH} width={barW} height={barH} rx={4}
              fill={COLORS[i % COLORS.length]} opacity={0.85} />
            <text x={x + barW / 2} y={height - 28 - barH - 4} textAnchor="middle"
              className="fill-foreground text-[9px] font-bold">{d.value}</text>
            <text x={x + barW / 2} y={height - 12} textAnchor="middle"
              className="fill-muted-foreground text-[8px]">{d.label.slice(0, 6)}</text>
          </g>
        );
      })}
    </svg>
  );
};

// Score trend line
const TrendLine = ({ data, width = 320, height = 120 }) => {
  if (!data.length) return <p className="text-xs text-muted-foreground text-center py-6">No exam scores yet</p>;
  const max = Math.max(...data.map(d => d.score), 100);
  const min = 0;
  const pts = data.map((d, i) => {
    const x = 30 + (i / Math.max(data.length - 1, 1)) * (width - 50);
    const y = height - 25 - ((d.score - min) / (max - min)) * (height - 40);
    return { x, y, ...d };
  });
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  return (
    <svg width={width} height={height} className="mx-auto">
      <path d={linePath} fill="none" stroke="#6366f1" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={4} fill="#6366f1" stroke="white" strokeWidth={2} />
          <text x={p.x} y={p.y - 8} textAnchor="middle" className="fill-foreground text-[9px] font-bold">{p.score}%</text>
        </g>
      ))}
      {/* Y axis labels */}
      <text x={4} y={15} className="fill-muted-foreground text-[8px]">{max}%</text>
      <text x={4} y={height - 25} className="fill-muted-foreground text-[8px]">0%</text>
    </svg>
  );
};

// Radar chart for subject performance
const RadarChart = ({ data, size = 200 }) => {
  if (!data.length) return <p className="text-xs text-muted-foreground text-center py-6">No subject data yet</p>;
  const center = size / 2;
  const radius = size * 0.35;
  const n = data.length;
  const angleStep = (2 * Math.PI) / n;

  const getPoint = (i, value) => {
    const angle = angleStep * i - Math.PI / 2;
    const r = (value / 100) * radius;
    return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
  };

  const polygonPts = data.map((d, i) => getPoint(i, d.avg_score));
  const path = polygonPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';

  return (
    <svg width={size} height={size} className="mx-auto">
      {/* Grid rings */}
      {[25, 50, 75, 100].map(v => {
        const r = (v / 100) * radius;
        return <circle key={v} cx={center} cy={center} r={r} fill="none" stroke="currentColor" opacity={0.08} />;
      })}
      {/* Axes */}
      {data.map((_, i) => {
        const end = getPoint(i, 100);
        return <line key={i} x1={center} y1={center} x2={end.x} y2={end.y} stroke="currentColor" opacity={0.1} />;
      })}
      {/* Data polygon */}
      <path d={path} fill="#6366f1" fillOpacity={0.2} stroke="#6366f1" strokeWidth={2} />
      {/* Dots + labels */}
      {data.map((d, i) => {
        const p = getPoint(i, d.avg_score);
        const lp = getPoint(i, 115);
        return (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={3.5} fill="#6366f1" />
            <text x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle"
              className="fill-foreground text-[9px] font-medium">{d.subject.slice(0, 8)}</text>
          </g>
        );
      })}
    </svg>
  );
};

// Activity heatmap (last 28 days)
const ActivityHeatmap = ({ data }) => {
  const days = [];
  const now = new Date();
  for (let i = 27; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({ date: key, count: data[key] || 0, dayLabel: d.toLocaleDateString('en', { weekday: 'short' }).charAt(0) });
  }
  const max = Math.max(...days.map(d => d.count), 1);
  return (
    <div className="flex flex-wrap gap-1">
      {days.map((d, i) => (
        <div key={i} title={`${d.date}: ${d.count} activities`}
          className="w-5 h-5 rounded-sm transition-colors"
          style={{
            backgroundColor: d.count === 0
              ? 'var(--muted)' 
              : `rgba(99,102,241,${0.2 + (d.count / max) * 0.8})`
          }} />
      ))}
    </div>
  );
};


// Per-exam performance panel — surfaces best score, latest score, delta,
// attempts, gap-to-target (default 80%) and a friendly tip.  Purely a
// presentational component driven by the shared analytics payload.
function ExamProgressPanel({ perf }) {
  const trend = perf?.score_trend || [];
  const comps = perf?.competitive_exams || {};

  // Aggregate exam-attempt scores per course/exam title from the trend feed.
  const byExam = useMemo(() => {
    const map = {};
    trend.forEach((t) => {
      const key = t.course || 'Course Exam';
      if (!map[key]) map[key] = { name: key, best: 0, latest: 0, first: null, attempts: 0, scores: [] };
      map[key].scores.push(t.score);
      map[key].attempts += 1;
      map[key].best = Math.max(map[key].best, t.score);
      map[key].latest = t.score;
      if (map[key].first === null) map[key].first = t.score;
    });
    return Object.values(map);
  }, [trend]);

  // Merge in competitive exams too.
  const compRows = Object.entries(comps).map(([eid, data]) => ({
    name: eid.replace(/_/g, ' ').toUpperCase(),
    best: Math.round(data.best),
    latest: Math.round(data.scores?.[data.scores.length - 1] || 0),
    first: Math.round(data.scores?.[0] || 0),
    attempts: data.attempts,
    scores: data.scores,
    competitive: true,
  }));

  const rows = [...byExam, ...compRows];

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="w-4 h-4 text-orange-500" />Exam Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground text-center py-6">No exam attempts yet. Take an exam or mock test to see your progress here.</p>
        </CardContent>
      </Card>
    );
  }

  const TARGET = 80;
  const bestOverall = Math.max(...rows.map((r) => r.best));
  const weakest = rows.reduce((a, b) => (a.best <= b.best ? a : b));
  const strongest = rows.reduce((a, b) => (a.best >= b.best ? a : b));

  return (
    <Card data-testid="exam-progress-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Target className="w-4 h-4 text-orange-500" />Exam Progress
          <Badge variant="outline" className="text-[9px] ml-auto">Target: {TARGET}%</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Highlight strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="rounded-lg border p-3 bg-emerald-500/5 border-emerald-500/30" data-testid="exam-strongest">
            <p className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400 font-semibold">Strongest</p>
            <p className="text-sm font-bold truncate mt-0.5">{strongest.name}</p>
            <p className="text-lg font-black text-emerald-500">{strongest.best}%</p>
          </div>
          <div className="rounded-lg border p-3 bg-amber-500/5 border-amber-500/30" data-testid="exam-target-gap">
            <p className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 font-semibold">Gap to Target</p>
            <p className="text-sm font-bold truncate mt-0.5">{TARGET - bestOverall > 0 ? `${TARGET - bestOverall}% away` : 'Target hit'}</p>
            <p className="text-lg font-black text-amber-500">{bestOverall}%</p>
          </div>
          <div className="rounded-lg border p-3 bg-red-500/5 border-red-500/30" data-testid="exam-weakest">
            <p className="text-[10px] uppercase tracking-wide text-red-600 dark:text-red-400 font-semibold">Needs Practice</p>
            <p className="text-sm font-bold truncate mt-0.5">{weakest.name}</p>
            <p className="text-lg font-black text-red-500">{weakest.best}%</p>
          </div>
        </div>

        {/* Per-exam rows */}
        <div className="space-y-2">
          {rows.slice(0, 12).map((r, i) => {
            const delta = r.latest - r.first;
            const gap = Math.max(0, TARGET - r.best);
            const trendColour = delta > 0 ? 'text-emerald-500' : delta < 0 ? 'text-red-500' : 'text-muted-foreground';
            const trendSym = delta > 0 ? '▲' : delta < 0 ? '▼' : '·';
            return (
              <div key={i} className="p-3 rounded-lg border bg-card" data-testid={`exam-row-${i}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate flex items-center gap-2">
                      <span>{r.name}</span>
                      {r.competitive && <Badge variant="outline" className="text-[9px]">Competitive</Badge>}
                    </div>
                    <p className="text-[10px] text-muted-foreground">{r.attempts} attempt{r.attempts !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <div>
                      <p className="text-[9px] text-muted-foreground uppercase">Best</p>
                      <p className="text-sm font-bold text-emerald-500">{r.best}%</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-muted-foreground uppercase">Latest</p>
                      <p className="text-sm font-bold">{r.latest}%</p>
                    </div>
                    <div className={`text-xs font-semibold ${trendColour}`}>{trendSym} {Math.abs(delta)}</div>
                  </div>
                </div>
                <Progress value={r.best} className="h-1.5" />
                <div className="mt-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>{gap > 0 ? `${gap}% away from ${TARGET}% target` : 'Target reached'}</span>
                  <span>
                    {r.best >= 80 ? 'Solid — try a harder mock next.' :
                      r.best >= 60 ? 'Review the questions you missed most.' :
                        'Revise the weak chapters and re-attempt.'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


export default function AnalyticsDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [perf, setPerf] = useState(null);
  const [classData, setClassData] = useState(null);
  const [activeTab, setActiveTab] = useState('student');
  const isAdmin = user?.role && ['super_admin', 'org_admin', 'faculty'].includes(user.role);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const myPerf = await analyticsApi.getMyPerformance();
      setPerf(myPerf);
      if (isAdmin) {
        try {
          const cls = await analyticsApi.getClassOverview();
          setClassData(cls);
        } catch {}
      }
    } catch (e) {
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  };

  const overview = perf?.overview || {};

  // Student View
  const StudentView = () => (
    <div className="space-y-6" data-testid="student-analytics">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="analytics-stats">
        {[
          { icon: BookOpen, label: 'Courses', value: overview.total_courses || 0, color: 'from-violet-500 to-purple-600' },
          { icon: CheckCircle, label: 'Completed', value: overview.completed_courses || 0, color: 'from-emerald-500 to-green-600' },
          { icon: Target, label: 'Avg Score', value: `${overview.avg_exam_score || 0}%`, color: 'from-orange-500 to-red-500' },
          { icon: Star, label: 'Points', value: overview.total_points || 0, color: 'from-amber-500 to-yellow-600' },
          { icon: Beaker, label: 'Labs Done', value: overview.labs_completed || 0, color: 'from-cyan-500 to-blue-600' },
        ].map(({ icon: Icon, label, value, color }) => (
          <Card key={label} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold">{value}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Score Trend */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-500" />Score Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <TrendLine data={perf?.score_trend || []} width={380} height={130} />
          </CardContent>
        </Card>

        {/* Subject Performance Radar */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Brain className="w-4 h-4 text-violet-500" />Subject Strengths</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            <RadarChart data={perf?.subject_performance || []} size={220} />
          </CardContent>
        </Card>

        {/* Activity Heatmap */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4 text-green-500" />Activity (Last 28 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityHeatmap data={perf?.activity_heatmap || {}} />
            <p className="text-[10px] text-muted-foreground mt-2">Each square = 1 day. Darker = more activity.</p>
          </CardContent>
        </Card>

        {/* Gamification Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" />Gamification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 rounded-lg bg-muted/50">
                <p className="text-lg font-bold text-amber-500">{overview.total_points || 0}</p>
                <p className="text-[10px] text-muted-foreground">Points</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-muted/50">
                <p className="text-lg font-bold text-violet-500">{overview.chapters_completed || 0}</p>
                <p className="text-[10px] text-muted-foreground">Chapters</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-muted/50">
                <p className="text-lg font-bold text-emerald-500">{overview.exams_passed || 0}</p>
                <p className="text-[10px] text-muted-foreground">Passed</p>
              </div>
            </div>
            {perf?.badges?.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1">Badges</p>
                <div className="flex flex-wrap gap-1.5">
                  {perf.badges.map((b, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] gap-1"><Award className="w-3 h-3" />{b.name || b.badge_id}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Exam Progress — per-exam best vs latest, deltas, target gap, tips */}
      <ExamProgressPanel perf={perf} />

      {/* Competitive Exam Scores */}
      {perf?.recent_competitive_scores?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Recent Competitive Exam Scores</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {perf.recent_competitive_scores.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                      s.percentage >= 80 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                      s.percentage >= 50 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>{s.percentage}%</div>
                    <div>
                      <p className="text-sm font-medium">{s.exam_id?.replace(/_/g, ' ').toUpperCase()}</p>
                      <p className="text-[10px] text-muted-foreground">{s.mode === 'real' ? 'Real Exam' : 'Quick Answer'} - {s.correct}/{s.total} correct</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{new Date(s.submitted_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Course Enrollments */}
      {perf?.enrollments?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><BookOpen className="w-4 h-4 text-blue-500" />Course Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {perf.enrollments.map((e, i) => {
                const pct = e.total_chapters ? Math.round((e.chapters_completed / e.total_chapters) * 100) : 0;
                return (
                  <div key={i} className="p-3 rounded-lg border bg-card">
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-sm font-medium leading-tight">{e.course_title}</p>
                      <Badge variant={e.status === 'completed' ? 'default' : 'secondary'} className="text-[9px] shrink-0 ml-2">{e.status}</Badge>
                    </div>
                    <Progress value={pct} className="h-1.5 mb-1" />
                    <p className="text-[10px] text-muted-foreground">{e.chapters_completed}/{e.total_chapters} chapters ({pct}%)</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Admin/Teacher View
  const AdminView = () => {
    if (!classData) return <p className="text-center text-muted-foreground py-8">No class data available.</p>;
    return (
      <div className="space-y-6" data-testid="admin-analytics">
        {/* Class Overview Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Users, label: 'Students', value: classData.total_students, color: 'from-violet-500 to-purple-600' },
            { icon: Target, label: 'Avg Score', value: `${classData.class_avg_score}%`, color: 'from-orange-500 to-red-500' },
            { icon: Percent, label: 'Pass Rate', value: `${classData.pass_rate}%`, color: 'from-emerald-500 to-green-600' },
            { icon: Activity, label: 'Exams Taken', value: classData.total_exams_taken, color: 'from-blue-500 to-cyan-600' },
          ].map(({ icon: Icon, label, value, color }) => (
            <Card key={label}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{value}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Subject Averages */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-indigo-500" />Subject Averages</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart data={(classData.subject_averages || []).map(s => ({ label: s.subject, value: s.avg_score }))} width={360} height={160} />
            </CardContent>
          </Card>

          {/* Score Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-pink-500" />Score Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart data={Object.entries(classData.score_distribution || {}).map(([k, v]) => ({ label: k, value: v }))} width={360} height={160} />
            </CardContent>
          </Card>

          {/* Top Performers */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Top Performers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {(classData.top_performers || []).slice(0, 10).map((s, i) => (
                  <div key={s.user_id} className="flex items-center gap-2 p-3 rounded-lg border bg-card">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      i === 0 ? 'bg-yellow-400 text-yellow-900' : i === 1 ? 'bg-slate-300 text-slate-700' : i === 2 ? 'bg-amber-700 text-white' : 'bg-muted text-muted-foreground'
                    }`}>{i + 1}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate">{s.name}</p>
                      <p className="text-[10px] text-muted-foreground">{s.points} pts - {s.avg_score}% avg</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="analytics-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold">Performance Analytics</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <div className="flex bg-muted rounded-lg p-0.5 mr-2" data-testid="analytics-tab-switch">
                <button onClick={() => setActiveTab('student')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${activeTab === 'student' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>
                  My Stats
                </button>
                <button onClick={() => setActiveTab('admin')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${activeTab === 'admin' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>
                  Class Overview
                </button>
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/profile')}><User className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          activeTab === 'student' ? <StudentView /> : <AdminView />
        )}
      </main>
    </div>
  );
}
